import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Locate, Loader2, MapPinOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import LocationHelpModal from "./LocationHelpModal";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Tile URL: use Mapbox tiles via raster API (no Web Worker / no postMessage problems).
// Falls back to OpenStreetMap if the Mapbox token is missing.
const tileUrl = MAPBOX_TOKEN
  ? `https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/{z}/{x}/{y}@2x?access_token=${MAPBOX_TOKEN}`
  : "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const tileAttribution = MAPBOX_TOKEN
  ? '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
  : '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

const TIJUANA_CENTER = [32.5149, -117.0382]; // [lat, lng] for Leaflet
const TIJUANA_ZOOM = 11;

// Haversine distance in km
function distanceKm(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Custom price-pill divIcon factory
function buildPriceIcon(price, active) {
  const ring = active
    ? "ring-2 ring-[#111111] scale-110"
    : "ring-1 ring-black/10";
  const html = `<div class="px-2.5 py-1 rounded-full bg-white shadow-md ${ring} text-xs font-bold text-neutral-900 whitespace-nowrap transition cursor-pointer hover:bg-[#111111] hover:text-white">$${(price || 0).toLocaleString()}</div>`;
  return L.divIcon({
    html,
    className: "rivones-price-marker",
    iconSize: null,
    iconAnchor: [22, 14],
  });
}

// Pulsing blue dot for user location
const userLocationIcon = L.divIcon({
  html: '<div class="relative w-4 h-4"><span class="absolute inset-0 rounded-full bg-cyan-400 opacity-60 animate-ping"></span><span class="relative block w-4 h-4 rounded-full bg-cyan-500 ring-2 ring-white shadow-md"></span></div>',
  className: "rivones-user-marker",
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

// React-leaflet helper: programmatically fit bounds when set.
function FitBounds({ bounds, padding = [60, 60], maxZoom = 13 }) {
  const map = useMap();
  useEffect(() => {
    if (!bounds || bounds.length === 0) return;
    try {
      map.fitBounds(bounds, { padding, maxZoom, animate: true });
    } catch {
      /* ignore */
    }
  }, [JSON.stringify(bounds), map, padding, maxZoom]);
  return null;
}

// React-leaflet helper: invalidate size after layout settles.
function ResizeOnMount() {
  const map = useMap();
  useEffect(() => {
    const t1 = setTimeout(() => map.invalidateSize(), 100);
    const t2 = setTimeout(() => map.invalidateSize(), 600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [map]);
  // Watch for parent container resize too (list ↔ map toggle case)
  useEffect(() => {
    const c = map.getContainer();
    const ro = new ResizeObserver(() => {
      try { map.invalidateSize(); } catch { /* ignore */ }
    });
    ro.observe(c);
    return () => ro.disconnect();
  }, [map]);
  return null;
}

/**
 * SearchMap — Leaflet map with priced vehicle markers + "Find cars near me".
 *
 * Props mirror the old Mapbox version exactly so the rest of the app needs no changes:
 *  - vehicles: array of vehicle docs (must have location_lat / location_lng)
 *  - hoveredId / selectedId — visually highlight that marker
 *  - onMarkerHover(vehicle | null)
 *  - onMarkerClick(vehicle)
 *  - onUserLocation({lat, lng}) — fires when user location is acquired
 *  - className
 */
export default function SearchMap({
  vehicles = [],
  hoveredId,
  selectedId,
  onMarkerHover,
  onMarkerClick,
  onUserLocation,
  className = "",
}) {
  const { t } = useTranslation();
  const [userPos, setUserPos] = useState(null);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState("");
  const [bounds, setBounds] = useState(null);
  const fittedInitial = useRef(false);

  const points = useMemo(
    () =>
      (vehicles || []).filter(
        (v) =>
          typeof v.location_lat === "number" &&
          typeof v.location_lng === "number" &&
          !isNaN(v.location_lat) &&
          !isNaN(v.location_lng),
      ),
    [vehicles],
  );

  // Initial fit: zoom to all vehicles on first load. Defer setBounds out of
  // the effect's sync phase so React's strict lint doesn't flag setState
  // inside an effect — semantically identical for our one-shot fit.
  useEffect(() => {
    if (fittedInitial.current) return;
    if (points.length === 0) return;
    const next = points.map((v) => [v.location_lat, v.location_lng]);
    fittedInitial.current = true;
    const t = setTimeout(() => setBounds(next), 0);
    return () => clearTimeout(t);
  }, [points]);

  const nearestCount = useMemo(() => {
    if (!userPos || points.length === 0) return 0;
    return points.filter(
      (v) => distanceKm(userPos.lat, userPos.lng, v.location_lat, v.location_lng) <= 50,
    ).length;
  }, [userPos, points]);

  const [permissionState, setPermissionState] = useState(null); // 'granted'|'denied'|'prompt'
  const [helpOpen, setHelpOpen] = useState(false);

  // Probe the Permissions API on mount so we can preemptively show the iOS
  // recovery instructions when Safari already remembers a "Never" decision.
  useEffect(() => {
    if (!("permissions" in navigator) || !navigator.permissions?.query) return;
    let status;
    let cancelled = false;
    const onChange = () => {
      if (!cancelled && status) setPermissionState(status.state);
    };
    navigator.permissions
      .query({ name: "geolocation" })
      .then((s) => {
        if (cancelled) return;
        status = s;
        setPermissionState(s.state);
        s.onchange = onChange;
      })
      .catch(() => {
        /* Safari < 16 doesn't support permissions.query for geolocation */
      });
    return () => {
      cancelled = true;
      if (status) status.onchange = null;
    };
  }, []);

  const isIosSafari = () => {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent || "";
    const isIos = /iPad|iPhone|iPod/.test(ua) ||
      (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
    return isIos && isSafari;
  };

  const handleFindNearMe = () => {
    if (!("geolocation" in navigator)) {
      setLocateError(t("searchMap.noGeo"));
      return;
    }
    // If iOS already remembers a denial, getCurrentPosition() silently rejects
    // without surfacing the system prompt. Show recovery instructions instead
    // of pretending the permission can still be requested.
    if (permissionState === "denied") {
      setLocateError(
        isIosSafari() ? t("searchMap.permBlockedIos") : t("searchMap.permBlocked"),
      );
      setHelpOpen(true);
      return;
    }
    setLocating(true);
    setLocateError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setUserPos({ lat, lng });
        onUserLocation?.({ lat, lng });
        // Build bounds: user + nearest 8 cars in 50 km
        const nearby = points
          .map((v) => ({ v, d: distanceKm(lat, lng, v.location_lat, v.location_lng) }))
          .filter((x) => x.d <= 50)
          .sort((a, b) => a.d - b.d)
          .slice(0, 8);
        const newBounds = [[lat, lng], ...nearby.map((x) => [x.v.location_lat, x.v.location_lng])];
        setBounds(newBounds);
      },
      (err) => {
        setLocating(false);
        if (err.code === 1) {
          setPermissionState("denied");
          setLocateError(
            isIosSafari() ? t("searchMap.permBlockedIos") : t("searchMap.permBlocked"),
          );
          setHelpOpen(true);
        } else if (err.code === 2) {
          setLocateError(t("searchMap.noLocation"));
        } else if (err.code === 3) {
          setLocateError(t("searchMap.timeout"));
        } else {
          setLocateError(t("searchMap.genericError"));
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  return (
    <div
      className={`relative rounded-2xl overflow-hidden border border-neutral-200 bg-neutral-100 ${className}`}
      data-testid="search-map-wrapper"
    >
      <MapContainer
        center={TIJUANA_CENTER}
        zoom={TIJUANA_ZOOM}
        scrollWheelZoom={true}
        zoomControl={true}
        className="w-full h-full"
        data-testid="search-map"
        style={{ minHeight: "100%", height: "100%" }}
      >
        <TileLayer url={tileUrl} attribution={tileAttribution} maxZoom={19} />
        <ResizeOnMount />
        {bounds && bounds.length > 0 && <FitBounds bounds={bounds} />}

        {points.map((v) => (
          <Marker
            key={v.vehicle_id}
            position={[v.location_lat, v.location_lng]}
            icon={buildPriceIcon(v.daily_rate, v.vehicle_id === hoveredId || v.vehicle_id === selectedId)}
            eventHandlers={{
              mouseover: () => onMarkerHover?.(v),
              mouseout: () => onMarkerHover?.(null),
              click: () => onMarkerClick?.(v),
            }}
          >
            <Popup>
              <div style={{ width: 200, fontFamily: "Inter,sans-serif" }}>
                {v.photos?.[0] && (
                  <img
                    src={v.photos[0]}
                    alt={v.make}
                    style={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 8 }}
                  />
                )}
                <div style={{ padding: "6px 4px 2px" }}>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: 13 }}>
                    {v.year} {v.make} {v.model}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: 11, color: "#666" }}>
                    ⭐ {(v.rating || 0).toFixed(1)} ({v.review_count || 0}) · ${(v.daily_rate || 0).toLocaleString()}{t("searchMap.perDay")}
                  </p>
                </div>
                <a
                  href={`/cars/${v.vehicle_id}`}
                  style={{
                    display: "block",
                    marginTop: 6,
                    textAlign: "center",
                    background: "#111",
                    color: "#fff",
                    padding: "6px 0",
                    borderRadius: 6,
                    textDecoration: "none",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {t("searchMap.viewDetails")}
                </a>
              </div>
            </Popup>
          </Marker>
        ))}

        {userPos && (
          <Marker position={[userPos.lat, userPos.lng]} icon={userLocationIcon}>
            <Popup>{t("searchMap.yourLocation")}</Popup>
          </Marker>
        )}
      </MapContainer>

      {/* No vehicles overlay */}
      {points.length === 0 && (
        <div
          className="absolute top-3 left-1/2 -translate-x-1/2 z-[400] px-3 py-1.5 rounded-full bg-white/95 shadow-sm ring-1 ring-black/5 text-xs font-medium text-neutral-700 flex items-center gap-2"
          data-testid="search-map-no-cars"
        >
          <MapPinOff className="w-3.5 h-3.5 text-neutral-400" />
          {t("searchMap.noCarsZone")}
        </div>
      )}

      {/* Find Cars Near Me floating FAB.
       *
       * `bottom` formula keeps the button (and its sibling status pill) well
       * above the mobile BottomNav (≈80 px) plus the iOS home-indicator safe
       * area, so users can't accidentally hit Explorar / Guía / Mapa / Perfil
       * when reaching for "Buscar cerca de mí". Desktop ignores BottomNav
       * (lg:hidden) so we collapse back to a tighter `1.25rem` there.
       */}
      <button
        type="button"
        onClick={handleFindNearMe}
        disabled={locating}
        data-testid="find-near-me-btn"
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 7rem)",
        }}
        className="absolute left-4 z-[400] inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#111111] hover:bg-neutral-800 text-white text-sm font-semibold shadow-lg ring-1 ring-black/10 active:scale-95 transition disabled:opacity-70 disabled:cursor-wait lg:!bottom-5"
      >
        {locating ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Locate className="w-4 h-4 text-cyan-400" />
        )}
        <span>{locating ? t("searchMap.searching") : t("searchMap.findNearMe")}</span>
      </button>

      {/* Status pill: shows nearest count or error */}
      {(nearestCount > 0 || locateError) && (
        <div
          className="absolute right-4 z-[400] max-w-[65%] sm:max-w-[55%] lg:!bottom-5"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 7rem)" }}
          data-testid="near-me-status"
        >
          {locateError ? (
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              data-testid="near-me-error"
              className="block text-left px-3 py-2 rounded-2xl bg-red-50 text-red-800 text-xs font-medium shadow-md ring-1 ring-red-200 leading-snug hover:bg-red-100 active:scale-[0.99] transition cursor-pointer"
            >
              {locateError}
            </button>
          ) : (
            <p className="px-3 py-1.5 rounded-full bg-white text-[#111111] text-xs font-semibold shadow-sm ring-1 ring-black/5">
              {nearestCount} {nearestCount === 1 ? "auto cercano" : "autos cercanos"}
            </p>
          )}
        </div>
      )}

      <LocationHelpModal open={helpOpen} onOpenChange={setHelpOpen} />
    </div>
  );
}
